import React, { createContext, useContext, useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { getValidToken } from '../utils/auth';
import { getMarketStatus } from '../utils/marketStatus';

const WS_URL = process.env.REACT_APP_WS_URL || `wss://${window.location.host}/ws`;

const MarketDataContext = createContext(null);

export function MarketDataProvider({ children }) {
  const [tickers, setTickers] = useState({});
  // Live per-contract options-table overlay (bid/ask/last/volume), keyed by
  // OCC symbol, and live underlying spot prices keyed by stock symbol - both
  // pushed by the backend's WS-tick path (ws/options_stream.go,
  // ws/alpaca_stream.go EnsureTrades) instead of REST polling. Consumed by
  // GenericOptionsWidget/OptionsWidget to overlay onto their REST-fetched
  // contract list rather than re-polling it.
  const [optionTicks, setOptionTicks] = useState({});
  const [underlyingTicks, setUnderlyingTicks] = useState({});
  // Ticks arrive far faster than anyone can read them - SPX alone streams
  // hundreds of contracts. Calling setState per tick re-rendered every
  // context consumer on every message, and on the Options/Options-WS pages
  // (where GenericOptionsWidget recomputes several useMemos across the whole
  // table) that saturated the main thread: clicking a nav link changed the
  // URL but React never got an idle moment to process the route change, so
  // the page appeared frozen on Options with the wrong link highlighted.
  // Ticks are buffered in refs here and flushed on a timer instead.
  const pendingOptionTicks = useRef({});
  const pendingUnderlyingTicks = useRef({});
  // Enhanced S&P 500 page streams (real SPX via put-call parity, 0DTE
  // call/put flow, high-volume S/R zones) - see backend ws/spx_live.go.
  const [spxLive, setSpxLive] = useState(null);
  const [spxFlow, setSpxFlow] = useState(null);
  const [spxZones, setSpxZones] = useState(null);
  const [connected, setConnected] = useState(false);
  const [marketStatus, setMarketStatus] = useState(getMarketStatus());
  const [shortSymbols, setShortSymbols] = useState(new Set());
  const [breakoutLongs, setBreakoutLongs] = useState([]);
  const [breakoutShorts, setBreakoutShorts] = useState([]);
  const [top5History, setTop5History] = useState([]);
  const [convergenceSignals, setConvergenceSignals] = useState([]);
  const [updownWeight, setUpdownWeight] = useState({
    green1m: 0, red1m: 0, greenCount1m: 0, redCount1m: 0,
    green5m: 0, red5m: 0, greenCount5m: 0, redCount5m: 0,
    green30m: 0, red30m: 0, greenCount30m: 0, redCount30m: 0,
    minuteTS: null,
  });
  const wsRef = useRef(null);
  const reconnectTimer = useRef(null);
  const mountedRef = useRef(true);

  // Update market status every 10 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      setMarketStatus(getMarketStatus());
    }, 10000);
    return () => clearInterval(interval);
  }, []);

  const connect = useCallback(async () => {
    if (!mountedRef.current) return;

    const token = await getValidToken();
    if (!token) {
      // Token refresh failed due to network error (auth failures redirect to login).
      // Schedule a retry so the reconnect loop doesn't break.
      if (mountedRef.current) {
        reconnectTimer.current = setTimeout(connect, 3000);
      }
      return;
    }
    if (!mountedRef.current) return;

    const ws = new WebSocket(`${WS_URL}?token=${token}`);
    wsRef.current = ws;

    ws.onopen = () => {
      if (mountedRef.current) setConnected(true);
    };

    ws.onmessage = (event) => {
      try {
        const messages = JSON.parse(event.data);

        // Handle breakout_signals event (2 tables: longs + shorts)
        if (messages && !Array.isArray(messages) && messages.ev === 'breakout_signals') {
          setBreakoutLongs(messages.longs || []);
          setBreakoutShorts(messages.shorts || []);
          return;
        }

        // Handle convergence_signals event
        if (messages && !Array.isArray(messages) && messages.ev === 'convergence_signals') {
          setConvergenceSignals(messages.signals || []);
          return;
        }

        // Handle top5_history event
        if (messages && !Array.isArray(messages) && messages.ev === 'top5_history') {
          setTop5History(messages.entries || []);
          return;
        }

        // Handle short_symbols event (object, not array)
        if (messages && !Array.isArray(messages) && messages.ev === 'short_symbols') {
          setShortSymbols(new Set(messages.symbols));
          return;
        }

        // Handle option_tick event (per-contract live bid/ask/last/volume).
        // Buffered, NOT setState'd per tick - see flushTicks below.
        if (messages && !Array.isArray(messages) && messages.ev === 'option_tick') {
          pendingOptionTicks.current[messages.sym] = {
            bid: messages.bid, ask: messages.ask, last: messages.last, volume: messages.volume,
          };
          return;
        }

        // Handle underlying_trade event (live spot price for an options-table underlying)
        if (messages && !Array.isArray(messages) && messages.ev === 'underlying_trade') {
          pendingUnderlyingTicks.current[messages.sym] = messages.price;
          return;
        }

        // Enhanced S&P 500 page streams (ws/spx_live.go). Low cadence
        // (2s/30s/60s), so direct setState is fine - no buffering needed.
        if (messages && !Array.isArray(messages) && messages.ev === 'spx_price') {
          setSpxLive({ price: messages.price, src: messages.src, ts: messages.ts });
          return;
        }
        if (messages && !Array.isArray(messages) && messages.ev === 'spx_flow') {
          setSpxFlow(messages);
          return;
        }
        if (messages && !Array.isArray(messages) && messages.ev === 'spx_zones') {
          setSpxZones(messages);
          return;
        }

        // Handle up/down by weight event
        if (messages && !Array.isArray(messages) && messages.ev === 'updown_weight') {
          setUpdownWeight({
            green1m: messages.green_1m || 0, red1m: messages.red_1m || 0,
            greenCount1m: messages.green_count_1m || 0, redCount1m: messages.red_count_1m || 0,
            green5m: messages.green_5m || 0, red5m: messages.red_5m || 0,
            greenCount5m: messages.green_count_5m || 0, redCount5m: messages.red_count_5m || 0,
            green30m: messages.green_30m || 0, red30m: messages.red_30m || 0,
            greenCount30m: messages.green_count_30m || 0, redCount30m: messages.red_count_30m || 0,
            minuteTS: messages.minute_ts || null,
          });
          return;
        }

        if (!Array.isArray(messages)) return;

        setTickers((prev) => {
          const updated = { ...prev };
          for (const msg of messages) {
            if (msg.ev !== 'AM') continue;
            if (!msg.op || msg.op <= 0) continue;

            const changePercent = ((msg.c - msg.op) / msg.op) * 100;

            updated[msg.sym] = {
              sym: msg.sym,
              price: msg.c,
              open: msg.op,
              changePercent: changePercent,
              volume: msg.v,
              timestamp: msg.s,
              premarketOpen: msg.pm_op || 0,
              mainOpen: msg.mm_op || 0,
            };
          }
          return updated;
        });
      } catch (e) {
        // ignore parse errors
      }
    };

    ws.onclose = () => {
      if (mountedRef.current) {
        setConnected(false);
        wsRef.current = null;
        reconnectTimer.current = setTimeout(connect, 3000);
      }
    };

    ws.onerror = () => {
      ws.close();
    };
  }, []);

  useEffect(() => {
    mountedRef.current = true;
    connect();
    return () => {
      mountedRef.current = false;
      if (wsRef.current) wsRef.current.close();
      if (reconnectTimer.current) clearTimeout(reconnectTimer.current);
    };
  }, [connect]);

  // Flush buffered ticks ~4x/second. Fast enough to still read as live,
  // but it collapses hundreds of messages into one render instead of
  // hundreds. Only sets state when something actually arrived, so a quiet
  // market costs nothing.
  useEffect(() => {
    const id = setInterval(() => {
      const opt = pendingOptionTicks.current;
      if (Object.keys(opt).length > 0) {
        pendingOptionTicks.current = {};
        setOptionTicks((prev) => ({ ...prev, ...opt }));
      }
      const und = pendingUnderlyingTicks.current;
      if (Object.keys(und).length > 0) {
        pendingUnderlyingTicks.current = {};
        setUnderlyingTicks((prev) => ({ ...prev, ...und }));
      }
    }, 250);
    return () => clearInterval(id);
  }, []);

  // Memoised so consumers don't re-render just because the provider did -
  // without this, `value={{...}}` built a fresh object every render and
  // every consumer of any field re-rendered on every unrelated update.
  const value = useMemo(
    () => ({ tickers, connected, marketStatus, shortSymbols, breakoutLongs, breakoutShorts,
             top5History, convergenceSignals, updownWeight, optionTicks, underlyingTicks,
             spxLive, spxFlow, spxZones }),
    [tickers, connected, marketStatus, shortSymbols, breakoutLongs, breakoutShorts,
     top5History, convergenceSignals, updownWeight, optionTicks, underlyingTicks,
     spxLive, spxFlow, spxZones],
  );

  return (
    <MarketDataContext.Provider value={value}>
      {children}
    </MarketDataContext.Provider>
  );
}

export function useMarketContext() {
  const context = useContext(MarketDataContext);
  if (!context) {
    throw new Error('useMarketContext must be used within MarketDataProvider');
  }
  return context;
}
